import React, { useEffect, useMemo } from 'react';

import { ActionIcon, Divider, Drawer, Skeleton, Stack, Tabs, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronLeft, IconMenu2 } from '@tabler/icons-react';
import { ReactComponent as KiloLogo } from 'assets/svg/long_kilo_blue_white.svg';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import LocationMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/LocationMultiSelectAutoComplete';
import StaffMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/StaffMultiSelectAutoComplete';
import useSelfBookingAppointmentTypes from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentTypes';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface FilterTerms {
  eventTypes: IEventTypeMultiSelectAutoComplete[];
  coaches: string[];
  locations: string[];
}

type BookingStep = 'appointmentType' | 'coach' | 'date' | 'timeSlot';

interface ScheduleHeaderProps {
  searchFilters?: FilterTerms;
  setSearchFilters?: React.Dispatch<React.SetStateAction<FilterTerms>>;
  currentStep?: BookingStep;
  onStepBack?: () => void;
  showAppointmentBackButton?: boolean;
}

const ScheduleHeader = ({
  searchFilters,
  setSearchFilters,
  onStepBack,
  showAppointmentBackButton = false,
}: ScheduleHeaderProps): JSX.Element => {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const {
    data: selfBookableAppointmentTypesData,
    isLoading: isSelfBookableAppointmentTypesLoading,
    isSuccess: isSelfBookableAppointmentTypesSuccess,
  } = useSelfBookingAppointmentTypes();

  const activeTabSearchParam = searchParams.get('activeTab');
  const activeTab = useMemo(() => {
    if (pathname !== '/') {
      return 'appointment';
    }

    return activeTabSearchParam ? activeTabSearchParam : 'class';
  }, [activeTabSearchParam, pathname]);

  const [isFiltersModalOpened, { open: openFiltersDrawer, close: closeFiltersDrawer }] =
    useDisclosure(false);

  const handleEventTypesChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    if (setSearchFilters) {
      setSearchFilters(prev => ({ ...prev, eventTypes: values }));
    }
  };
  const handleCoachesChange = (values: string[]): void => {
    if (setSearchFilters) {
      setSearchFilters(prev => ({ ...prev, coaches: values }));
    }
  };
  const handleLocationsChange = (values: string[]): void => {
    if (setSearchFilters) {
      setSearchFilters(prev => ({ ...prev, locations: values }));
    }
  };

  const handleBackClick = (): void => {
    if (state && state.prevPath) {
      navigate(state.prevPath, { state: state });
    } else {
      navigate(-1);
    }
  };

  const appointmentTabExist = selfBookableAppointmentTypesData?.pages.some(p => !!p.data.count);

  useEffect(() => {
    if (isSelfBookableAppointmentTypesSuccess && !appointmentTabExist) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentTabExist, isSelfBookableAppointmentTypesSuccess]);

  const shouldShowBackButton = !searchFilters || showAppointmentBackButton;
  const shouldShowFiltersButton = searchFilters && !showAppointmentBackButton;

  const handleAppointmentBackClick = (): void => {
    if (onStepBack) {
      onStepBack();
    }
  };

  return (
    <>
      <div className='header'>
        {shouldShowBackButton && (
          <ActionIcon
            variant='light'
            radius='lg'
            size='xl'
            onClick={showAppointmentBackButton ? handleAppointmentBackClick : handleBackClick}
          >
            <IconChevronLeft size='1.625rem' />
          </ActionIcon>
        )}
        {shouldShowFiltersButton && (
          <ActionIcon color='kiloBlue' variant='filled' radius='md' size='lg' onClick={openFiltersDrawer}>
            <IconMenu2 size='1.625rem' />
          </ActionIcon>
        )}
        <Title order={3} color='gray.8' align='center'>
          Schedule
        </Title>
        <KiloLogo height='100%' width={50} />
      </div>

      {searchFilters && (
        <Drawer
          opened={isFiltersModalOpened}
          onClose={closeFiltersDrawer}
          title={<Title order={2}>Filters</Title>}
          position='bottom'
        >
          <Stack>
            <Divider mx='-xl' />

            <EventTypeMultiSelectAutoComplete
              handleChange={handleEventTypesChange}
              values={searchFilters.eventTypes}
              size='lg'
            />
            <LocationMultiSelectAutoComplete
              onChange={handleLocationsChange}
              value={searchFilters.locations}
              size='lg'
            />
            <StaffMultiSelectAutoComplete
              onChange={handleCoachesChange}
              value={searchFilters.coaches}
              isCoach
              size='lg'
            />
          </Stack>
        </Drawer>
      )}

      {isSelfBookableAppointmentTypesLoading && <Skeleton h='32px' />}
      {isSelfBookableAppointmentTypesSuccess && selfBookableAppointmentTypesData?.pages && (
        <Tabs
          variant='pills'
          value={activeTab}
          onTabChange={value => navigate(`/?activeTab=${value}`)}
          mt='xl'
          mb='md'
        >
          <Tabs.List position='center'>
            <Tabs.Tab value='class'>Classes</Tabs.Tab>
            {appointmentTabExist && <Tabs.Tab value='appointment'>Appointments</Tabs.Tab>}
          </Tabs.List>
        </Tabs>
      )}
    </>
  );
};

export default ScheduleHeader;
